import React, { useState, useRef } from 'react';
import styles from './RegisterCardModal.module.css';

import { InnocodePaymentPasswordButton, InnocodePaymentPasswordDot } from '@intsoft/innocode-payment';
import { InnocodeButton, InnocodeCheckbox, InnocodeInput, InnocodeRadiobox, useDialog } from '@intsoft/innocode-core';
import { getCookie } from '../../utils/cookies/cookies';
import { ZENPAY_BILLING } from '../../utils/Config';
import axios from "../../utils/api/axios";


type PasswordMode = 'create' | 'modify' | 'input';

/* 비밀번호 입력/생성/수정 모달 */
interface RegisterCardModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm?: (data: any) => void;
}

export const RegisterCardModal: React.FC<RegisterCardModalProps> = (props: RegisterCardModalProps) => {
    const { showDialog } = useDialog();
    const [selectedPaymentType, setPaymentType] = useState("personal");
    const [cardNum1, setCardNum1] = useState('');
    const [cardNum2, setCardNum2] = useState('');
    const [cardNum3, setCardNum3] = useState('');
    const [cardNum4, setCardNum4] = useState('');
    const [expiry, setExpiry] = useState('');
    const [cvc, setCvc] = useState('');
    const [password, setPassword] = useState('');
    const [term1, setTerm1] = useState(false);
    const [term2, setTerm2] = useState(false);
    const [term3, setTerm3] = useState(false);

    // Refs for auto-focus
    const cardNum1Ref = useRef<HTMLInputElement>(null);
    const cardNum2Ref = useRef<HTMLInputElement>(null);
    const cardNum3Ref = useRef<HTMLInputElement>(null);
    const cardNum4Ref = useRef<HTMLInputElement>(null);
    const expiryRef = useRef<HTMLInputElement>(null);
    const cvcRef = useRef<HTMLInputElement>(null);
    const passwordRef = useRef<HTMLInputElement>(null);
    const cvcRegex = /^\d{3}$/;
    // Assuming 2 digits for password verification as is common, or generic number check
    // User asked for regex check, I'll assume 2 digits based on typical KR flows, but let's be safe with just digits if length varies.
    // However, usually it's "First 2 digits". I'll stick to 2 digits for now.
    const pwRegex = /^\d{2}$/;

    const onlyNumbers = (value: string) => value.replace(/\D/g, '');

    const handleNumericInput = (
        value: string,
        maxLength: number,
        setter: (val: string) => void,
        nextRef?: React.RefObject<HTMLInputElement | null>,
        type?: string
    ) => {
        const numericValue = onlyNumbers(value);


        if (numericValue.length <= maxLength) {

            if (type === "expiry") {
                const formattedValue = numericValue.replace(/^(\d{2})(\d{2})$/, "$1/$2");
                setter(formattedValue);
            } else {
                setter(numericValue);
            }
            // 최대 길이 도달 시 다음 input으로 포커스
            if (numericValue.length === maxLength && nextRef?.current) {
                setTimeout(() => nextRef.current?.focus(), 0);
            }
        }
    };

    /**
     * 다양한 형식의 생년월일 문자열을 'yyMMdd' 포맷으로 정규화합니다.
     * - 지원 형식: YYYY-MM-DD, YYYYMMDD, YYMMDD, YY-MM-DD, YYYY.MM.DD 등
     * - 유효하지 않은 형식이거나 값이 없으면 null을 반환합니다.
     * @param {string | null | undefined} birthdate - 변환할 생년월일 문자열
     * @returns {string | null} 'yyMMdd' 형식의 문자열 또는 null
     */
    const normalizeBirthdateToYYMMDD = (birthdate: string | null | undefined) => {
        // 1. 입력값이 유효한 문자열인지 확인
        if (!birthdate || typeof birthdate !== 'string') {
            return null;
        }

        // 2. 앞뒤 공백 제거 및 구분자 통일
        const cleanDate = birthdate.trim().replace(/\./g, '-'); // '.'을 '-'로 변경

        // 3. 정규표현식 패턴 정의 (우선순위 순서대로)
        const patterns = [
            // YYYY-MM-DD (e.g., "1993-04-21")
            /^(\d{4})-(\d{2})-(\d{2})$/,
            // YYYYMMDD (e.g., "19930421")
            /^(\d{4})(\d{2})(\d{2})$/,
            // YY-MM-DD (e.g., "93-04-21")
            /^(\d{2})-(\d{2})-(\d{2})$/,
            // YYMMDD (e.g., "930421")
            /^(\d{2})(\d{2})(\d{2})$/,
        ];

        // 4. 패턴 순회하며 일치하는 형식 찾기
        for (const pattern of patterns) {
            const match = cleanDate.match(pattern);

            if (match) {
                // match 결과: ['전체문자열', '년', '월', '일']
                let year = match[1];
                const month = match[2];
                const day = match[3];

                // 5. 4자리 연도를 2자리로 변환
                if (year.length === 4) {
                    year = year.slice(-2); // 뒤의 두 자리만 사용 (e.g., "1993" -> "93")
                }

                // 6. 'yyMMdd' 포맷으로 조합하여 반환
                return `${year}${month}${day}`;
            }
        }

        // 7. 어떤 패턴과도 일치하지 않으면 null 반환
        return null;
    };

    const handleConfirm = async () => {
        if (!selectedPaymentType || !cardNum1 || !cardNum2 || !cardNum3 || !cardNum4 || !expiry || !cvc || !password || !term1 || !term2 || !term3) {
            showDialog({
                type: 'info',
                iconType: 'info',
                title: '필수 정보를\n입력해주세요.',
                message: '필수 정보를 입력해주세요.',
                confirmText: '확인',
                onConfirm: () => {

                }
            })
            return;
        }

        // 카드번호 16자리 숫자 체크
        const rawCardNumber = `${cardNum1}${cardNum2}${cardNum3}${cardNum4}`;
        if (rawCardNumber && !/^\d{16}$/.test(rawCardNumber)) {
            showDialog({
                type: 'info',
                iconType: 'info',
                title: '카드번호를\n확인해주세요.',
                message: '카드번호를 확인해주세요.',
                confirmText: '확인',
                onConfirm: () => {

                }
            })
            return;
        }

        // 유효기간 MM/YY 체크
        const month = expiry.split("/")[0];
        const year = expiry.split("/")[1];
        const currentYearShort = new Date().getFullYear().toString().slice(-2); // 예: '24'
        const currentMonthShort = new Date().getMonth() + 1; // 예: '24'

        if (
            (!month || !year) ||
            (month.length !== 2 || year.length !== 2) ||
            (month && (Number(month) < 1 || Number(month) > 12)) || //월
            (year && (Number(year) < Number(currentYearShort))) || //년
            (month && year && (Number(month) < Number(currentMonthShort)) && Number(year) === Number(currentYearShort)) //년월
        ) {
            showDialog({
                type: 'info',
                iconType: 'info',
                title: '유효기간을\n확인해주세요.',
                message: '유효기간을 확인해주세요.',
                confirmText: '확인',
                onConfirm: () => {

                }
            })
            return;
        }

        // CVC 3자리 숫자 체크
        if (cvc && !cvcRegex.test(cvc)) {
            showDialog({
                type: 'info',
                iconType: 'info',
                title: 'CVC를\n확인해주세요.',
                message: 'CVC를 확인해주세요.\n 3자리 숫자로 입력해주세요.',
                confirmText: '확인',
                onConfirm: () => {

                }
            })
            return;
        }

        // 비밀번호 2자리 숫자 체크
        if (password && !pwRegex.test(password)) {
            showDialog({
                type: 'info',
                iconType: 'info',
                title: '비밀번호를\n확인해주세요.',
                message: '비밀번호를 확인해주세요.\n 2자리 숫자로 입력해주세요.',
                confirmText: '확인',
                onConfirm: () => {

                }
            })
            return;
        }

        const user = JSON.parse(getCookie("user"));

        const cardData = {
            unifiedProfileId: getCookie("user_id"),
            card: {
                number: `${cardNum1}${cardNum2}${cardNum3}${cardNum4}`,
                expiryYear: year,
                expiryMonth: month,
                birthOrBusinessRegistrationNumber: normalizeBirthdateToYYMMDD(user.birthdate),
                passwordTwoDigits: password
            },
            fullName: user.name,
            terms: {
                term1,
                term2,
                term3
            }
        };

        const response = await axios.post(`${ZENPAY_BILLING}/create-blil-key`, cardData,
            {
                headers: {
                    'Content-Type': 'application/json',
                    'API-KEY': `zentapay`
                }
            }
        );

        if (props.onConfirm) {
            props.onConfirm(response.data);
        } else {
            // Fallback or debug
            console.error("Registered Card Data:", cardData);
        }
        props.onClose();
    };

    return (
        <div className={styles.layoutContainer}>
            <div className={styles.editorHeader}>
                <div className={styles.editorHeaderCloseButton} onClick={props.onClose}>
                    <svg className="w-6 h-6 text-gray-800" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" onClick={props.onClose} style={{ cursor: 'pointer' }}>
                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18 17.94 6M18 18 6.06 6" />
                    </svg>
                </div>
            </div>
            <div className={styles.scrollContentArea}>
                <div className={styles.minHeightArea}>
                    <div className={styles.contentAreaWrapper}>
                        <div className={styles.contentArea}>
                            <div className={styles.contentAreaTitle}>
                                <h1 className={`innocode-typography--headline-sm innocode-typography--bold ${styles.contentAreaTitleText}`}>
                                    새 결제카드 등록
                                </h1>
                            </div>
                            <div className={styles.registerFormArea}>
                                <div className={styles.registTypeArea}>
                                    <InnocodeRadiobox checked={selectedPaymentType === "personal"} leadingIcon={true} title={"개인"} value={"personal"} onChange={(value) => setPaymentType("personal")}></InnocodeRadiobox>
                                    <InnocodeRadiobox checked={selectedPaymentType === "corporate"} leadingIcon={true} title={"법인"} value={"corporate"} onChange={(value) => setPaymentType("corporate")}></InnocodeRadiobox>
                                </div>
                                <div className={styles.optionBox}>
                                    <div className={styles.optionTitle}>
                                        <label className="innocode-input__label innocode-typography--body-md-reading innocode-typography--medium">카드번호<span className="innocode-input__required">*</span></label>
                                    </div>
                                    <div className={styles.registFormContentArea}>
                                        <InnocodeInput
                                            ref={cardNum1Ref}
                                            placeholder="****"
                                            value={cardNum1}
                                            onChange={(e) => handleNumericInput(e.target.value, 4, setCardNum1, cardNum2Ref)}
                                        >
                                        </InnocodeInput>
                                        <InnocodeInput
                                            ref={cardNum2Ref}
                                            placeholder="****"
                                            type="password"
                                            value={cardNum2}
                                            onChange={(e) => handleNumericInput(e.target.value, 4, setCardNum2, cardNum3Ref)}
                                        >
                                        </InnocodeInput>
                                        <InnocodeInput
                                            ref={cardNum3Ref}
                                            placeholder="****"
                                            type="password"
                                            value={cardNum3}
                                            onChange={(e) => handleNumericInput(e.target.value, 4, setCardNum3, cardNum4Ref)}
                                        >
                                        </InnocodeInput>
                                        <InnocodeInput
                                            ref={cardNum4Ref}
                                            placeholder="****"
                                            value={cardNum4}
                                            onChange={(e) => handleNumericInput(e.target.value, 4, setCardNum4, expiryRef)}
                                        >
                                        </InnocodeInput>
                                    </div>
                                </div>

                                <div className={styles.registFormContentArea}>
                                    <InnocodeInput
                                        ref={expiryRef}
                                        label="유효기간"
                                        required
                                        placeholder="MM/YY"
                                        type="text"
                                        value={expiry}
                                        onChange={(e) => handleNumericInput(e.target.value, 4, setExpiry, cvcRef, "expiry")}
                                    >
                                    </InnocodeInput>
                                    <InnocodeInput
                                        ref={cvcRef}
                                        label="CVC"
                                        required
                                        placeholder="카드 뒷면 3자리 숫자"
                                        type="password"
                                        value={cvc}
                                        onChange={(e) => handleNumericInput(e.target.value, 3, setCvc, passwordRef)}
                                    >
                                    </InnocodeInput>
                                </div>
                                <div className={styles.registFormContentArea}>
                                    <InnocodeInput
                                        ref={passwordRef}
                                        label="카드 비밀번호"
                                        required
                                        placeholder="카드 비밀번호 앞 2자리"
                                        type="password"
                                        value={password}
                                        onChange={(e) => handleNumericInput(e.target.value, 2, setPassword)}
                                    >
                                    </InnocodeInput>
                                </div>
                            </div>
                            <div className={styles.termArea}>
                                <InnocodeCheckbox leadingIcon={true} title="인트소프트 개인(신용)정보 수집 및 이용 동의" checked={term1} onChange={(value) => setTerm1(value)}></InnocodeCheckbox>
                                <InnocodeCheckbox leadingIcon={true} title="인트소프트 → 카드사 개인(신용)정보 제공 동의" checked={term2} onChange={(value) => setTerm2(value)}></InnocodeCheckbox>
                                <InnocodeCheckbox leadingIcon={true} title="카드사 → 인트소프트 개인(신용)정보 제공 동의" checked={term3} onChange={(value) => setTerm3(value)}></InnocodeCheckbox>
                            </div>

                        </div>
                        <div className={styles.actionButtonWrapper}>
                            <InnocodeButton size={"xl"} className={styles.actionButton} onClick={handleConfirm}>등록하기</InnocodeButton>
                        </div>
                    </div>
                </div>
            </div>
        </div >
    );
};